/* @flow */
import {Observable} from 'rxjs/Observable';
import type {ActionsObservable} from 'redux-observable';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/merge';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/of';

import type {ReportsResponse} from 'nutshell-core/types';
import {
    jsonToTimeseriesReportData,
    jsonToReportTableData,
    jsonToReportTableTotalsData,
    addReferenceData,
} from 'nutshell-core/json-to-report-data';

import {
    updateSiteAnalyticsReportTableData,
    updateSiteAnalyticsReportChartData,
    updateAnalyticsSummaryStats,
    failSiteAnalyticsReportChartData,
    type SiteAnalyticsReportDataRequestedAction,
} from './analytics-actions';

import {
    getSiteAnalyticsReportPostParams,
    type SiteAnalyticsReportPostParams,
} from './analytics-utils';

export const requestSiteAnalyticsReportDataEpic = (action$: ActionsObservable<*>) =>
    action$
        .ofType('SITE_ANALYTICS_REPORT_DATA_REQUESTED')
        .switchMap((action: SiteAnalyticsReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getSiteAnalyticsReportPostParams(reportParams, filters);

            const requestStream = Observable.fromPromise(fetchData(params));
            const currentResultStream = requestStream.map((res) => res[0]);
            const reportDataStream = requestStream.map(transformDataForReport);

            const reportTableDataStream = reportDataStream.map(({tableData}) =>
                updateSiteAnalyticsReportTableData(tableData)
            );

            const reportChartDataStream = reportDataStream
                .map((data) => data.chartData)
                .filter((chartData) => Boolean(chartData))
                .map((chartData) => updateSiteAnalyticsReportChartData({chartData}));

            const summaryStatStream = currentResultStream.map((currentResult) =>
                updateAnalyticsSummaryStats(currentResult.meta)
            );

            return reportChartDataStream
                .merge(summaryStatStream.merge(reportTableDataStream))
                .catch((err) => Observable.of(failSiteAnalyticsReportChartData(err)));
        });

function fetchData(params: SiteAnalyticsReportPostParams) {
    return Promise.all([fetchSiteAnalyticsReportData(params)]);
}

function fetchSiteAnalyticsReportData(params: SiteAnalyticsReportPostParams): Promise<any> {
    const url = '/rest/mcfxvisits/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any); // This is how we have to convince flow that the response is a ReportsResponse
}

// Taken from report-epics
function transformDataForReport(results: ReportsResponse[]) {
    let chartData;
    const currentResult = results[0];
    if (!currentResult || !Array.isArray(currentResult.reports))
        return {chartData: null, tableData: null};
    const comparisonResult = results.length > 1 && results[1];

    const currentChartData = jsonToTimeseriesReportData(currentResult);
    if (comparisonResult) {
        const referenceData = jsonToTimeseriesReportData(comparisonResult);
        chartData = addReferenceData(currentChartData, referenceData);
    } else {
        chartData = currentChartData;
    }

    const rows = jsonToReportTableData(currentResult);
    const totals = jsonToReportTableTotalsData(currentResult);
    const tableData = {rows, totals};

    return {chartData, tableData};
}
